import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { usePathParam, useSecondOrderCallback, useSwitch, useViewport } from '@chilecompra/react-kit'
import {
  Breadcrumb,
  Breadcrumbs,
  Button,
  Popover,
  PopoverMenuItem,
  Spinner,
  Switch,
  Tooltip,
  Typography
} from '@chilecompra/react-kit/components'
import { DEFAULT_SECTOR } from '../../config/settings/constants'
import routes from '../../config/settings/routes'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import SectorDataDialog from '../../components/Dialogs/SectorDataDialog'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import { onGetSectorThunk } from './SectorSection.actions'
import { SECTORS_DICTIONARY, SECTORS_NUMBERS_DICTIONARY } from '../BuyersSectors/BuyersSectors.utils'
import {
  Action,
  ActionLink,
  Anchor,
  AnchorIcon,
  AnchorText,
  BreadcrumbsBackground,
  Card,
  Content,
  Datum,
  DownloadIcon,
  ErrorMessage,
  GridContainer,
  GridItem,
  HelpIcon,
  Info,
  Jumbotron,
  JumboContent,
  Label,
  Line,
  Root,
  Strong,
  Title,
  TitleBackground,
  TopDatum,
  Underline,
  Value,
  YearSelectorBackground
} from './SectorSection.styles'

/**
 * The SectorSection's container.
 */
const SectorSection = props => {
  const { children } = props
  const dispatch = useDispatch()
  const theme = useTheme()
  const navigate = useNavigate()
  const sectorSlug = usePathParam('sectorSlug') || DEFAULT_SECTOR
  const { size } = useViewport()
  const { amount, amountUSD, biddings, error, loading, name, orders, organizations, purchases } = useSelector(
    state => state.sectorSection
  )
  const { endYear, loading: initialLoading, startYear } = useSelector(state => state.topNavbar)
  const years = useMemo(() => generateYearsBetween(endYear, startYear), [])
  const [helpOpen, setHelpOpen] = useState(false)
  const [yearsOpen, setYearsOpen] = useState(false)
  const [yearValue, setYearValue] = useState(endYear)
  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()
  const createHandleYearChange = useSecondOrderCallback(
    (_, updatedValue) => {
      setYearValue(updatedValue)
      setYearsOpen(false)
      dispatch(onGetSectorThunk({ id: SECTORS_NUMBERS_DICTIONARY[sectorSlug], year: updatedValue }))
    },
    [sectorSlug]
  )
  const handleCloseHelp = useCallback(() => setHelpOpen(false), [])
  const handleOpenHelp = useCallback(() => setHelpOpen(true), [])
  const handleCloseYears = useCallback(() => setYearsOpen(false), [])
  const handleOpenYears = useCallback(() => setYearsOpen(true), [])
  const handleRedirectToDownloads = useCallback(
    () =>
      navigate(`${routes.downloadOrderAndBiddings}#DescargaReportes`, {
        state: { name, idEntity: SECTORS_NUMBERS_DICTIONARY[sectorSlug] }
      }),
    []
  )
  const handleRedirectToHome = useCallback(() => navigate(routes.root, { replace: true }))

  useEffect(() => {
    if (initialLoading || !sectorSlug) {
      return
    }

    setYearValue(endYear)
    dispatch(onGetSectorThunk({ id: SECTORS_NUMBERS_DICTIONARY[sectorSlug], year: endYear }))
  }, [endYear, initialLoading, sectorSlug])

  return (
    <Root height={loading || !!error ? '100%' : 'auto'}>
      {(initialLoading || loading) && (
        <GridContainer alignItems="center" height="100%">
          <GridItem align="center" xs={12}>
            <Spinner />
          </GridItem>
        </GridContainer>
      )}
      {!!error && !initialLoading && !loading && (
        <GridContainer alignItems="center" height="100%">
          <GridItem align="center" xs={12}>
            <ErrorMessage>
              No existe información asociada al sector “<Strong>{sectorSlug}</Strong>”
            </ErrorMessage>
            <Button color="primary" onClick={handleRedirectToHome}>
              Volver al Inicio
            </Button>
          </GridItem>
        </GridContainer>
      )}
      {!error && !initialLoading && !loading && (
        <>
          <GridContainer>
            <GridItem xs={12}>
              <Jumbotron height="400px" source={SECTORS_DICTIONARY[SECTORS_NUMBERS_DICTIONARY[sectorSlug]].banner}>
                <JumboContent>
                  <BreadcrumbsBackground>
                    <Breadcrumbs>
                      <Breadcrumb to={routes.root} variant="link">
                        Datos Abiertos
                      </Breadcrumb>
                      <Breadcrumb to={routes.organizations} variant="link">
                        Organismos Compradores
                      </Breadcrumb>
                      <Breadcrumb>Sectores del Estado</Breadcrumb>
                    </Breadcrumbs>
                  </BreadcrumbsBackground>
                  <Title>
                    <TitleBackground>{name}</TitleBackground>
                  </Title>
                </JumboContent>
              </Jumbotron>
            </GridItem>
          </GridContainer>
          <Content>
            <GridContainer margin="0 auto" maxWidth="740px">
              <GridItem md={8} xs={12}>
                <YearSelectorBackground>
                  <Typography display="inline" fontSize="16px" margin="0 10px 0 0" variant="body2">
                    Cifras acumuladas durante el año
                  </Typography>
                  <Popover
                    anchor={
                      <Anchor>
                        <AnchorText>{yearValue}</AnchorText>
                        <AnchorIcon open={yearsOpen} />
                      </Anchor>
                    }
                    open={yearsOpen}
                    onClose={handleCloseYears}
                    onOpen={handleOpenYears}
                  >
                    {years.map(year => (
                      <PopoverMenuItem enableClose key={year.value} onClick={createHandleYearChange(year.value)}>
                        {year.name}
                      </PopoverMenuItem>
                    ))}
                  </Popover>
                </YearSelectorBackground>
              </GridItem>
            </GridContainer>
            <GridContainer margin="0 auto" maxWidth="740px">
              <GridItem margin="7px 0 0" xs={12}>
                <Card color="blank" elevation="1">
                  <GridContainer
                    backgroundColor={theme.palette.brand.primary}
                    borderTopLeftRadius="4px"
                    borderTopRightRadius="4px"
                    margin="0 0 32px"
                    padding="14px 16px"
                  >
                    <GridItem align={size.isDownToMedium ? 'center' : 'right'} md={6} padding="0 8px" xs={12}>
                      <Tooltip
                        content={`Número total de organismos compradores que forman parte del sector ${name}`}
                        placement="bottom"
                      >
                        <TopDatum>
                          <Strong>{organizations}</Strong> <Underline>Organismos registrados</Underline>
                        </TopDatum>
                      </Tooltip>
                    </GridItem>
                    <GridItem align={size.isDownToMedium ? 'center' : 'left'} md={6} padding="0 8px" xs={12}>
                      <Tooltip
                        content="Número de organismos compradores que realizaron almenos una compra durante el año."
                        placement="top"
                      >
                        <TopDatum>
                          <Strong>{purchases}</Strong> <Underline>compraron durante el año {yearValue}</Underline>
                        </TopDatum>
                      </Tooltip>
                    </GridItem>
                  </GridContainer>
                  <GridContainer padding="0 16px" spacing={1}>
                    {amount !== undefined ? (
                      <>
                        <GridItem height="60px" md={5} xs={12}>
                          <Info>
                            <Datum>
                              <Value>${formatAmount(parseInt((currencyValue ? amountUSD : amount) ?? 0, 10))}</Value>
                              <Label margin="7px 0 0">Monto total transado durante el año</Label>
                            </Datum>
                          </Info>
                        </GridItem>
                        <GridItem height="60px" md={3} xs={12}>
                          <Info>
                            <Line />
                            <Datum>
                              <Value>{formatAmount(parseInt(orders ?? 0, 10))}</Value>
                              <Label margin="7px 0 0">Órdenes de compra</Label>
                            </Datum>
                          </Info>
                        </GridItem>
                        <GridItem height="60px" md={4} xs={12}>
                          <Info>
                            <Line />
                            <Datum>
                              <Value>{formatAmount(parseInt(biddings ?? 0, 10))}</Value>
                              <Label margin="7px 0 0">Licitaciones adjudicadas</Label>
                            </Datum>
                          </Info>
                        </GridItem>
                      </>
                    ) : (
                      <>
                        <GridItem height="30px" md={12} xs={12}>
                          <Typography align="center" color="black2" variant="h4">
                            No existe información para el año seleccionado.
                          </Typography>
                        </GridItem>
                      </>
                    )}
                  </GridContainer>
                  <GridContainer alignItems="flex-end" padding="45px 16px 24px" spacing={1}>
                    <GridItem md={9} xs={12}>
                      <Action>
                        <ActionLink onClick={handleOpenHelp}>
                          <HelpIcon>?</HelpIcon>
                          En qué se basa la información de este sector
                        </ActionLink>
                      </Action>
                      <Action margin="6px 0 0">
                        <ProtectByFeatures rule={features => features['cl.chilecompra.datos-abiertos/downloads-page']}>
                          <ActionLink onClick={handleRedirectToDownloads}>
                            <DownloadIcon />
                            Descargas masivas de este sector
                          </ActionLink>
                        </ProtectByFeatures>
                      </Action>
                    </GridItem>
                    <GridItem
                      md={3}
                      textAlign={size.isDownToMedium ? 'center' : 'right'}
                      margin={size.isDownToMedium ? '24px 0 32px' : '0'}
                      xs={12}
                    >
                      <Switch
                        checked={currencyValue}
                        color="primary"
                        labelLeft="CLP"
                        labelRight="USD"
                        onChange={handleChangeCurrency}
                      />
                    </GridItem>
                  </GridContainer>
                </Card>
              </GridItem>
            </GridContainer>
          </Content>
          {children}
          <SectorDataDialog open={helpOpen} onClose={handleCloseHelp} />
        </>
      )}
    </Root>
  )
}

SectorSection.propTypes = {
  children: PropTypes.node.isRequired
}

export default SectorSection
